import React, { useState, useEffect, useCallback } from "react";
import DatePicker, { registerLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import "react-datepicker/dist/react-datepicker.css";
import {
  getCalendar,
  getUsersSummary,
  getAllGroups,
  getUserById,
} from "../../../api/api";
import {
  getLeaveTypes,
  addHrInspectorReport,
  addHrInspectorCompensationReport,
  addHrInspectorTravelReport,
  getHrInspectorReports,
  deleteHrInspectorReport,
} from "../../../api/reportsApi";
import {
  createSubstitution,
  getSubstitutions,
  exportSubstitutionsExcel,
} from "../../../api/substitutionsApi";
import "../../Account/Admin/SubNavigation/SubNavigation.css";
import "./VacationManagement.css";

registerLocale("ru", ru);

const isTravelOperation = (leaveType) => leaveType?.label === "Командировка";
const isSubstitutionOperation = (leaveType) => leaveType?.label === "Замещение";
const isCompensationOperation = (leaveType) => leaveType?.label === "Компенсация";

const MONTH_NAMES = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

const buildCompensationMonthOptions = () =>
  MONTH_NAMES.map((label, index) => ({
    value: index + 1,
    label,
  }));

const buildCompensationYearOptions = () => {
  const currentYear = new Date().getFullYear();
  return [currentYear - 1, currentYear, currentYear + 1].map((year) => ({
    value: year,
    label: String(year),
  }));
};

const compensationMonthOptions = buildCompensationMonthOptions();
const compensationYearOptions = buildCompensationYearOptions();
const compensationDayBasisOptions = [
  { value: "workdays", label: "Рабочие дни" },
  { value: "calendar", label: "Календарные дни" },
];

const getDefaultCompensationMonth = () => new Date().getMonth() + 1;

const getDefaultCompensationYear = () => new Date().getFullYear();

const getDefaultCompensationDayBasis = () => "workdays";

const formatDateForApi = (date) => date.toLocaleDateString("en-CA");

const getCurrentYearDateRange = () => {
  const year = new Date().getFullYear();
  return {
    startDate: new Date(year, 0, 1),
    endDate: new Date(year, 11, 31),
  };
};

/** Замещение — отдельный эндпоинт /substitutions/, не приходит из hr_inspector */
const SUBSTITUTION_TYPE = { value: "substitution", label: "Замещение" };

/** Командировка — отдельный эндпоинт add_travel_jobs, не приходит из hr_inspector/all */
const TRAVEL_TYPE = { value: "travel", label: "Командировка" };

/** Типы с бэкенда + локальные типы, которых нет в hr_inspector/all */
const mergeBackendTypesWithLocalTypes = (apiTypes = []) => {
  const fromApi = Array.isArray(apiTypes) ? apiTypes : [];
  const result = [...fromApi];
  if (!result.some((t) => t.label === "Командировка")) {
    result.push(TRAVEL_TYPE);
  }
  if (!result.some((t) => t.label === "Замещение")) {
    result.push(SUBSTITUTION_TYPE);
  }
  return result;
};

const selectMenuProps = {
  menuPortalTarget: document.body,
  menuPosition: "fixed",
  styles: {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  },
};

/** Только основной отпуск (ОТ), без БС, учебного отпуска, отгулов, больничных и командировок */
const VACATION_LEAVE_TYPE = "Отпуск";
const HR_INSPECTOR_PAGE_SIZE = 100;

const fetchAllHrInspectorReportsForUser = async (userId) => {
  let page = 1;
  let allReports = [];
  let hasMore = true;

  while (hasMore) {
    const response = await getHrInspectorReports({
      user_id: userId,
      page,
      page_size: HR_INSPECTOR_PAGE_SIZE,
    });
    const batch = response.reports || [];
    allReports = allReports.concat(batch);
    if (batch.length < HR_INSPECTOR_PAGE_SIZE) {
      hasMore = false;
    } else {
      page += 1;
    }
  }

  return allReports;
};

const groupVacationReportsIntoPeriods = (reports) => {
  const vacationReports = reports
    .filter(
      (report) =>
        report.fact_date_start &&
        report.portal_job_type_name === VACATION_LEAVE_TYPE
    )
    .sort(
      (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start)
    );

  const groupedReports = {};
  let currentGroup = null;

  vacationReports.forEach((report) => {
    const currentDate = new Date(report.fact_date_start);

    if (
      !currentGroup ||
      Math.abs(currentDate - new Date(currentGroup.endDate)) >
        24 * 60 * 60 * 1000
    ) {
      if (currentGroup) {
        const key = `${currentGroup.startDate}_${currentGroup.endDate}`;
        groupedReports[key] = currentGroup;
      }

      currentGroup = {
        startDate: report.fact_date_start,
        endDate: report.fact_date_start,
      };
    } else {
      currentGroup.endDate = report.fact_date_start;
    }
  });

  if (currentGroup) {
    const key = `${currentGroup.startDate}_${currentGroup.endDate}`;
    groupedReports[key] = currentGroup;
  }

  return Object.values(groupedReports);
};

const getChronologicalPeriodBounds = (period) => {
  const dateA = new Date(period.startDate);
  const dateB = new Date(period.endDate);
  dateA.setHours(0, 0, 0, 0);
  dateB.setHours(0, 0, 0, 0);

  return dateA <= dateB
    ? { from: dateA, to: dateB }
    : { from: dateB, to: dateA };
};

const countCalendarDaysInPeriod = (dateA, dateB) => {
  const { from, to } = getChronologicalPeriodBounds({
    startDate: dateA,
    endDate: dateB,
  });
  return Math.round((to - from) / (24 * 60 * 60 * 1000)) + 1;
};

const formatVacationPeriod = (period) => {
  const { from, to } = getChronologicalPeriodBounds(period);
  const fromLabel = from.toLocaleDateString("ru-RU");
  if (from.getTime() === to.getTime()) {
    return fromLabel;
  }
  return `${fromLabel} — ${to.toLocaleDateString("ru-RU")}`;
};

/** Группировка периодов по году начала; итог — календарные дни внутри года */
const formatEmployeeSelectOption = (user) => {
  const fullName = `${user.surname} ${user.name} ${user.patronymic || ""}`.trim();
  const label = user.department ? `${fullName} — ${user.department}` : fullName;

  return { value: user.id, label };
};

const formatSubstitutionUserName = (user) => {
  if (!user) return "—";
  return `${user.surname} ${user.name} ${user.patronymic || ""}`.trim();
};

const formatSubstitutionPeriod = (startDate, endDate) => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const startLabel = start.toLocaleDateString("ru-RU");
  if (startDate === endDate) {
    return startLabel;
  }
  return `${startLabel} — ${end.toLocaleDateString("ru-RU")}`;
};

const SUBSTITUTIONS_LOOKBACK_MONTHS = 2;

const parseSubstitutionDate = (dateString) => {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getSubstitutionsPeriodStartDate = () => {
  const date = new Date();
  date.setHours(0, 0, 0, 0);
  date.setMonth(date.getMonth() - SUBSTITUTIONS_LOOKBACK_MONTHS);
  return date;
};

/** Замещение пересекается с окном [2 месяца назад, сегодня] */
const isSubstitutionWithinLastTwoMonths = (item) => {
  const periodStart = getSubstitutionsPeriodStartDate();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const startDate = parseSubstitutionDate(item.start_date);
  const endDate = parseSubstitutionDate(item.end_date);

  return endDate >= periodStart && startDate <= today;
};

const sortSubstitutionsByStartDateDesc = (items) =>
  [...items].sort(
    (a, b) => new Date(b.start_date) - new Date(a.start_date)
  );

const groupVacationHistoryByYear = (periods) => {
  const periodsByYear = {};
  const totalsByYear = {};

  periods.forEach((period) => {
    const { from, to } = getChronologicalPeriodBounds(period);
    const displayYear = from.getFullYear();
    const days = countCalendarDaysInPeriod(from, to);

    if (!periodsByYear[displayYear]) {
      periodsByYear[displayYear] = [];
    }

    periodsByYear[displayYear].push({
      ...period,
      days,
      sortDate: from.getTime(),
    });

    const cursor = new Date(from);
    while (cursor <= to) {
      const year = cursor.getFullYear();
      totalsByYear[year] = (totalsByYear[year] || 0) + 1;
      cursor.setDate(cursor.getDate() + 1);
    }
  });

  return Object.keys(periodsByYear)
    .map(Number)
    .sort((a, b) => b - a)
    .map((year) => ({
      year,
      periods: periodsByYear[year].sort((a, b) => b.sortDate - a.sortDate),
      totalDays: totalsByYear[year] || 0,
    }));
};

const VacationManagement = () => {
  const [activeTab, setActiveTab] = useState("create");
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [selectedDates, setSelectedDates] = useState({
    startDate: null,
    endDate: null,
  });
  const [selectedLeaveType, setSelectedLeaveType] = useState(null);
  const [calendarData, setCalendarData] = useState({});
  const [employees, setEmployees] = useState([]);
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [historyEmployees, setHistoryEmployees] = useState([]);
  const [isLoadingHistoryEmployees, setIsLoadingHistoryEmployees] =
    useState(false);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [selectedSubstituteDepartment, setSelectedSubstituteDepartment] =
    useState(null);
  const [substituteEmployees, setSubstituteEmployees] = useState([]);
  const [isLoadingSubstituteEmployees, setIsLoadingSubstituteEmployees] =
    useState(false);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [leaveTypes, setLeaveTypes] = useState(() =>
    mergeBackendTypesWithLocalTypes()
  );
  const [isLoadingLeaveTypes, setIsLoadingLeaveTypes] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = useState(false);
  const [pendingDeleteReportIds, setPendingDeleteReportIds] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [reports, setReports] = useState([]);
  const [isLoadingReports, setIsLoadingReports] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [selectedSubstituteEmployee, setSelectedSubstituteEmployee] =
    useState(null);
  const [substitutionReason, setSubstitutionReason] = useState("");
  const [substitutionDelegateApprovals, setSubstitutionDelegateApprovals] =
    useState(true);
  const [substitutionDelegateRoles, setSubstitutionDelegateRoles] =
    useState(true);
  const [substitutionComment, setSubstitutionComment] = useState("");
  const [substitutions, setSubstitutions] = useState([]);
  const [isLoadingSubstitutions, setIsLoadingSubstitutions] = useState(false);
  const [substitutionsError, setSubstitutionsError] = useState(null);
  const [isExportingSubstitutions, setIsExportingSubstitutions] =
    useState(false);
  const [showExportPopup, setShowExportPopup] = useState(false);
  const [exportDates, setExportDates] = useState(getCurrentYearDateRange);
  const [compensationDays, setCompensationDays] = useState("");
  const [compensationMonth, setCompensationMonth] = useState(
    getDefaultCompensationMonth()
  );
  const [compensationYear, setCompensationYear] = useState(
    getDefaultCompensationYear()
  );
  const [compensationDayBasis, setCompensationDayBasis] = useState(
    getDefaultCompensationDayBasis()
  );

  const subTabs = [
    { id: "create", label: "СОЗДАТЬ" },
    { id: "edit", label: "РЕДАКТИРОВАТЬ" },
    { id: "all", label: "ВСЕ" },
    { id: "history", label: "ИСТОРИЯ ОТПУСКОВ" },
  ];

  // Сброс значений при смене вкладки
  useEffect(() => {
    setSelectedDepartment(null);
    setSelectedSubstituteDepartment(null);
    setSubstituteEmployees([]);
    setSelectedEmployee(null);
    setSelectedDates({ startDate: null, endDate: null });
    setSelectedLeaveType(null);
    setSelectedProject(null);
    setSelectedSubstituteEmployee(null);
    setSubstitutionReason("");
    setSubstitutionDelegateApprovals(true);
    setSubstitutionDelegateRoles(true);
    setSubstitutionComment("");
    setSubstitutions([]);
    setSubstitutionsError(null);
    setCompensationDays("");
    setCompensationMonth(getDefaultCompensationMonth());
    setCompensationYear(getDefaultCompensationYear());
    setCompensationDayBasis(getDefaultCompensationDayBasis());
    setReports([]);
    setError(null);
  }, [activeTab]);

  // Типы: ответ бэкенда + «Командировка» и «Замещение» (без фильтра по отделу/сотруднику)
  useEffect(() => {
    if (activeTab !== "create") {
      return;
    }

    const loadLeaveTypes = async () => {
      try {
        setIsLoadingLeaveTypes(true);
        const typesFromApi = await getLeaveTypes();
        setLeaveTypes(mergeBackendTypesWithLocalTypes(typesFromApi));
      } catch (error) {
        console.error("Ошибка при загрузке типов кадровых операций:", error);
        setLeaveTypes(mergeBackendTypesWithLocalTypes());
      } finally {
        setIsLoadingLeaveTypes(false);
      }
    };

    loadLeaveTypes();
  }, [activeTab]);

  // Синхронизация portal_job_type_id после выбора отдела и сотрудника
  useEffect(() => {
    const syncLeaveTypeId = async () => {
      if (
        activeTab !== "create" ||
        !selectedLeaveType ||
        isSubstitutionOperation(selectedLeaveType) ||
        !selectedDepartment ||
        !selectedEmployee
      ) {
        return;
      }

      try {
        const types = await getLeaveTypes({
          group_id: selectedDepartment.value,
          user_id: selectedEmployee.value,
        });
        const matched = types.find((t) => t.label === selectedLeaveType.label);
        if (matched && matched.value !== selectedLeaveType.value) {
          setSelectedLeaveType(matched);
        }
      } catch (error) {
        console.error("Ошибка при синхронизации типа операции:", error);
      }
    };

    syncLeaveTypeId();
  }, [activeTab, selectedDepartment, selectedEmployee, selectedLeaveType]);

  const handleLeaveTypeChange = (option) => {
    setSelectedLeaveType(option);
    setSelectedDepartment(null);
    setSelectedSubstituteDepartment(null);
    setSubstituteEmployees([]);
    setSelectedEmployee(null);
    setSelectedSubstituteEmployee(null);
    setSelectedDates({ startDate: null, endDate: null });
    setSelectedProject(null);
    setSubstitutionReason("");
    setSubstitutionDelegateApprovals(true);
    setSubstitutionDelegateRoles(true);
    setSubstitutionComment("");
    setCompensationDays("");
    setCompensationMonth(getDefaultCompensationMonth());
    setCompensationYear(getDefaultCompensationYear());
    setCompensationDayBasis(getDefaultCompensationDayBasis());
  };

  // Загрузка списка отделов
  useEffect(() => {
    const loadDepartments = async () => {
      try {
        setIsLoadingDepartments(true);
        const allGroups = await getAllGroups();
        const uniqueDepartments = allGroups
          .filter((group) => group.is_active)
          .map((group) => ({
            value: group.id,
            label: group.name,
          }));
        setDepartments(uniqueDepartments);
      } catch (error) {
        console.error("Ошибка при загрузке отделов:", error);
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    loadDepartments();
  }, []);

  // Сотрудники отдела отсутствующего (и общий список для остальных операций)
  useEffect(() => {
    if (activeTab === "history") {
      return;
    }

    const loadEmployees = async () => {
      if (!selectedDepartment) {
        setEmployees([]);
        setSelectedEmployee(null);
        if (!isSubstitutionOperation(selectedLeaveType)) {
          setSelectedSubstituteEmployee(null);
        }
        return;
      }

      try {
        setIsLoadingEmployees(true);
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 1000,
          groupId: selectedDepartment.value,
        });

        const formattedEmployees = response.users.map(formatEmployeeSelectOption);

        setEmployees(formattedEmployees);
        setSelectedEmployee(null);
        if (!isSubstitutionOperation(selectedLeaveType)) {
          setSelectedSubstituteEmployee(null);
        }
      } catch (error) {
        console.error("Ошибка при загрузке списка сотрудников:", error);
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    loadEmployees();
  }, [selectedDepartment, activeTab, selectedLeaveType]);

  // Сотрудники отдела замещающего (только для типа «Замещение»)
  useEffect(() => {
    if (
      activeTab !== "create" ||
      !isSubstitutionOperation(selectedLeaveType)
    ) {
      setSubstituteEmployees([]);
      return;
    }

    const loadSubstituteEmployees = async () => {
      if (!selectedSubstituteDepartment) {
        setSubstituteEmployees([]);
        setSelectedSubstituteEmployee(null);
        return;
      }

      try {
        setIsLoadingSubstituteEmployees(true);
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 1000,
          groupId: selectedSubstituteDepartment.value,
        });

        setSubstituteEmployees(
          response.users.map(formatEmployeeSelectOption)
        );
        setSelectedSubstituteEmployee(null);
      } catch (error) {
        console.error(
          "Ошибка при загрузке сотрудников отдела замещающего:",
          error
        );
        setSubstituteEmployees([]);
      } finally {
        setIsLoadingSubstituteEmployees(false);
      }
    };

    loadSubstituteEmployees();
  }, [selectedSubstituteDepartment, activeTab, selectedLeaveType]);

  // Все сотрудники для поиска по ФИО на вкладке «История отпусков»
  useEffect(() => {
    if (activeTab !== "history") {
      return;
    }

    const loadHistoryEmployees = async () => {
      try {
        setIsLoadingHistoryEmployees(true);
        const response = await getUsersSummary({
          isActive: true,
          pageSize: 10000,
        });

        const formattedEmployees = response.users
          .map(formatEmployeeSelectOption)
          .sort((a, b) => a.label.localeCompare(b.label, "ru"));

        setHistoryEmployees(formattedEmployees);
      } catch (loadError) {
        console.error("Ошибка при загрузке сотрудников:", loadError);
        setHistoryEmployees([]);
      } finally {
        setIsLoadingHistoryEmployees(false);
      }
    };

    loadHistoryEmployees();
  }, [activeTab]);

  // Загрузка данных календаря
  const loadCalendarData = useCallback(async () => {
    try {
      const currentDate = new Date();
      const firstDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() - 1,
        1
      );
      const lastDay = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth() + 12,
        0
      );

      const dateFrom = firstDay.toISOString().split("T")[0];
      const dateTo = lastDay.toISOString().split("T")[0];

      const data = await getCalendar(dateFrom, dateTo);
      const calendarMap = {};
      data.forEach((day) => {
        calendarMap[day.date] = day;
      });

      setCalendarData(calendarMap);
    } catch (error) {
      console.error("Ошибка при загрузке календаря:", error);
    }
  }, []);

  useEffect(() => {
    loadCalendarData();
  }, [loadCalendarData]);

  // Загрузка проектов пользователя
  useEffect(() => {
    const loadProjects = async () => {
      if (!selectedEmployee || !isTravelOperation(selectedLeaveType)) {
        setProjects([]);
        setSelectedProject(null);
        return;
      }

      try {
        setIsLoadingProjects(true);
        const response = await getUserById(selectedEmployee.value);

        // Проверяем структуру ответа
        console.log("Ответ API getUserById:", response);

        let userData;
        if (response && response.users && response.users.length > 0) {
          userData = response.users[0];
        } else if (response && response.id) {
          // Если API возвращает пользователя напрямую
          userData = response;
        } else {
          console.warn("Неожиданная структура ответа API:", response);
          setProjects([]);
          return;
        }

        if (userData && userData.projects && userData.projects.length > 0) {
          const userProjects = userData.projects.map((project) => ({
            value: project.id,
            label: project.cipher_pilot,
          }));
          setProjects(userProjects);
        } else {
          setProjects([]);
        }
      } catch (error) {
        console.error("Ошибка при загрузке проектов:", error);
        setProjects([]);
      } finally {
        setIsLoadingProjects(false);
      }
    };

    loadProjects();
  }, [selectedEmployee, selectedLeaveType]);

  const loadSubstitutions = useCallback(async () => {
    try {
      setIsLoadingSubstitutions(true);
      setSubstitutionsError(null);

      const params = {
        page: 1,
        page_size: 100,
        include_inactive: true,
      };
      if (selectedEmployee?.value) {
        params.absent_user_id = selectedEmployee.value;
      }

      const response = await getSubstitutions(params);
      const recentItems = sortSubstitutionsByStartDateDesc(
        (response.items || []).filter(isSubstitutionWithinLastTwoMonths)
      );
      setSubstitutions(recentItems);
    } catch (loadError) {
      console.error("Ошибка при загрузке замещений:", loadError);
      setSubstitutions([]);
      setSubstitutionsError(
        loadError.message || "Ошибка при загрузке замещений"
      );
    } finally {
      setIsLoadingSubstitutions(false);
    }
  }, [selectedEmployee]);

  useEffect(() => {
    if (
      activeTab !== "create" ||
      !isSubstitutionOperation(selectedLeaveType)
    ) {
      setSubstitutions([]);
      setSubstitutionsError(null);
      return;
    }

    loadSubstitutions();
  }, [activeTab, selectedLeaveType, loadSubstitutions]);

  const highlightDays = (date) => {
    const adjustedDate = new Date(date);
    adjustedDate.setHours(12, 0, 0, 0);

    const dateString = adjustedDate.toLocaleDateString("en-CA");
    const dayData = calendarData[dateString];

    if (!dayData) return "";

    const classes = [];

    // Добавляем класс для выходных всегда
    if (!dayData.workday_flag) {
      classes.push("highlight-weekend");
    }

    // Добавляем класс для событий
    if (dayData.event_name) {
      classes.push("has-event");
    }

    return classes.join(" ");
  };

  const filterDate = (date) => {
    // Убираем ограничение на выходные дни - разрешаем выбирать любые дни
    return true;
  };

  const handleDateChange = (date, type) => {
    if (type === "start") {
      if (selectedDates.endDate && date > selectedDates.endDate) {
        setSelectedDates({ startDate: date, endDate: null });
      } else {
        setSelectedDates((prev) => ({ ...prev, startDate: date }));
      }
    } else {
      setSelectedDates((prev) => ({ ...prev, endDate: date }));
    }
  };

  const getWorkingDaysInRange = (startDate, endDate) => {
    const workingDays = [];
    let currentDate = new Date(startDate);
    currentDate.setHours(0, 0, 0, 0);

    const endDateTime = new Date(endDate);
    endDateTime.setHours(23, 59, 59, 999);

    while (currentDate <= endDateTime) {
      const dateString = currentDate.toLocaleDateString("en-CA");
      const dayData = calendarData[dateString];

      if (dayData?.workday_flag) {
        workingDays.push(dateString);
      }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workingDays;
  };

  const handleSubmitSubstitution = async () => {
    if (
      !selectedDepartment ||
      !selectedEmployee ||
      !selectedSubstituteDepartment ||
      !selectedSubstituteEmployee ||
      !selectedDates.startDate ||
      !selectedDates.endDate ||
      !substitutionReason.trim()
    ) {
      alert("Пожалуйста, заполните все обязательные поля");
      return;
    }

    if (selectedEmployee.value === selectedSubstituteEmployee.value) {
      alert("Отсутствующий и замещающий сотрудник не могут совпадать");
      return;
    }

    try {
      setIsSubmitting(true);
      await createSubstitution({
        absent_user_id: selectedEmployee.value,
        substitute_user_id: selectedSubstituteEmployee.value,
        absent_user_group_id: selectedDepartment.value,
        substitute_user_group_id: selectedSubstituteDepartment.value,
        start_date: formatDateForApi(selectedDates.startDate),
        end_date: formatDateForApi(selectedDates.endDate),
        reason: substitutionReason.trim(),
        delegate_approvals: substitutionDelegateApprovals,
        delegate_roles: substitutionDelegateRoles,
        comment: substitutionComment.trim(),
      });

      setError(null);
      setShowPopup(true);
      await loadSubstitutions();

      setTimeout(() => {
        setShowPopup(false);
        setSelectedLeaveType(null);
        setSelectedDepartment(null);
        setSelectedSubstituteDepartment(null);
        setSubstituteEmployees([]);
        setSelectedEmployee(null);
        setSelectedSubstituteEmployee(null);
        setSelectedDates({ startDate: null, endDate: null });
        setSubstitutionReason("");
        setSubstitutionDelegateApprovals(true);
        setSubstitutionDelegateRoles(true);
        setSubstitutionComment("");
      }, 3000);
    } catch (err) {
      setError(`Ошибка при создании замещения: ${err.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitCompensation = async () => {
    const days = parseInt(compensationDays, 10);

    if (
      !selectedDepartment ||
      !selectedEmployee ||
      !selectedLeaveType ||
      !compensationMonth ||
      !compensationYear ||
      !compensationDayBasis ||
      !Number.isFinite(days) ||
      days < 1
    ) {
      alert("Пожалуйста, заполните все обязательные поля");
      return;
    }

    try {
      setIsSubmitting(true);

      const typesForUser = await getLeaveTypes({
        group_id: selectedDepartment.value,
        user_id: selectedEmployee.value,
      });
      const matchedType = typesForUser.find(
        (t) => t.label === selectedLeaveType.label
      );
      if (!matchedType) {
        alert("Выбранный тип недоступен для этого сотрудника");
        return;
      }

      await addHrInspectorCompensationReport({
        user_id: selectedEmployee.value,
        group_id: selectedDepartment.value,
        compensation_days: days,
        compensation_month: compensationMonth,
        compensation_year: compensationYear,
        compensation_day_basis: compensationDayBasis,
        portal_job_type_id: matchedType.value,
      });

      setError(null);
      setShowPopup(true);

      setTimeout(() => {
        setShowPopup(false);
        setSelectedEmployee(null);
        setSelectedLeaveType(null);
        setSelectedDepartment(null);
        setCompensationDays("");
        setCompensationMonth(getDefaultCompensationMonth());
        setCompensationYear(getDefaultCompensationYear());
        setCompensationDayBasis(getDefaultCompensationDayBasis());
      }, 3000);
    } catch (err) {
      setError(`Ошибка при создании компенсации: ${err.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async () => {
    if (isSubstitutionOperation(selectedLeaveType)) {
      await handleSubmitSubstitution();
      return;
    }

    if (isCompensationOperation(selectedLeaveType)) {
      await handleSubmitCompensation();
      return;
    }

    if (
      !selectedDepartment ||
      !selectedEmployee ||
      !selectedDates.startDate ||
      !selectedDates.endDate ||
      !selectedLeaveType ||
      (isTravelOperation(selectedLeaveType) && !selectedProject)
    ) {
      alert("Пожалуйста, заполните все поля");
      return;
    }

    try {
      setIsSubmitting(true);

      let leaveType = selectedLeaveType;
      const typesForUser = await getLeaveTypes({
        group_id: selectedDepartment.value,
        user_id: selectedEmployee.value,
      });
      const matchedType = typesForUser.find(
        (t) => t.label === selectedLeaveType.label
      );
      if (!matchedType) {
        alert("Выбранный тип недоступен для этого сотрудника");
        return;
      }
      leaveType = matchedType;

      const startDate = new Date(selectedDates.startDate);
      const endDate = new Date(selectedDates.endDate);

      // Для командировок используем только рабочие дни
      let period;
      if (isTravelOperation(leaveType)) {
        period = getWorkingDaysInRange(startDate, endDate);

        // Проверяем, что есть рабочие дни в выбранном периоде
        if (period.length === 0) {
          alert("В выбранном периоде нет рабочих дней");
          return;
        }
      } else {
        // Для других типов отпусков используем все дни
        period = [];
        let currentDate = new Date(startDate);
        while (currentDate <= endDate) {
          period.push(currentDate.toLocaleDateString("en-CA"));
          currentDate.setDate(currentDate.getDate() + 1);
        }
      }

      const reportData = {
        user_id: selectedEmployee.value,
        group_id: selectedDepartment.value,
        period: period,
        portal_job_type_id: leaveType.value,
        ...(isTravelOperation(leaveType) && {
          project_id: selectedProject.value,
        }),
      };

      console.log("Подготовленные данные для отправки:", reportData);

      if (isTravelOperation(leaveType)) {
        await addHrInspectorTravelReport(reportData);
      } else {
        await addHrInspectorReport(reportData);
      }

      setError(null);
      setShowPopup(true);

      // Сбрасываем форму через 3 секунды после успешного создания
      setTimeout(() => {
        setShowPopup(false);
        setSelectedEmployee(null);
        setSelectedDates({ startDate: null, endDate: null });
        setSelectedLeaveType(null);
        setSelectedProject(null);
        setSelectedDepartment(null);
      }, 3000);
    } catch (error) {
      setError(`Ошибка при создании отчета: ${error.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Загрузка отчетов для выбранного сотрудника
  useEffect(() => {
    const loadReports = async () => {
      if (!selectedEmployee || !selectedDepartment || activeTab !== "edit") {
        console.log("Пропускаем загрузку отчетов:", {
          selectedEmployee,
          selectedDepartment,
          activeTab,
        });
        setReports([]);
        return;
      }

      try {
        setIsLoadingReports(true);
        console.log("Начинаем загрузку отчетов с параметрами:", {
          user_id: selectedEmployee.value,
          group_id: selectedDepartment.value,
        });

        const response = await getHrInspectorReports({
          user_id: selectedEmployee.value,
          group_id: selectedDepartment.value,
          page_size: 1000,
        });

        console.log("Получены отчеты:", response);

        // Сначала сортируем отчеты по дате
        const sortedReports = [...response.reports].sort(
          (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start)
        );

        // Группировка отчетов по типам и периодам
        const groupedReports = {};
        let currentGroup = null;

        sortedReports.forEach((report) => {
          console.log("Обрабатываем отчет:", report);

          if (!report.fact_date_start || !report.portal_job_type_name) {
            console.warn(
              "Пропускаем отчет из-за отсутствия необходимых полей:",
              report
            );
            return;
          }

          const currentDate = new Date(report.fact_date_start);

          // Если это первый отчет или новый тип отпуска, создаем новую группу
          if (
            !currentGroup ||
            currentGroup.type !== report.portal_job_type_name ||
            Math.abs(currentDate - new Date(currentGroup.endDate)) >
              24 * 60 * 60 * 1000
          ) {
            // Если была предыдущая группа, сохраняем ее
            if (currentGroup) {
              const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}`;
              groupedReports[key] = currentGroup;
            }

            // Создаем новую группу
            currentGroup = {
              type: report.portal_job_type_name,
              startDate: report.fact_date_start,
              endDate: report.fact_date_start,
              reportIds: [report.id],
              leaveType: {
                label: report.portal_job_type_name,
              },
            };
          } else {
            // Продолжаем текущую группу
            currentGroup.endDate = report.fact_date_start;
            currentGroup.reportIds.push(report.id);
          }
        });

        // Не забываем добавить последнюю группу
        if (currentGroup) {
          const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}`;
          groupedReports[key] = currentGroup;
        }

        console.log("Сгруппированные отчеты:", groupedReports);
        setReports(Object.values(groupedReports));
      } catch (error) {
        console.error("Ошибка при загрузке отчетов:", error);
        setError("Ошибка при загрузке отчетов");
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadReports();
  }, [selectedEmployee, selectedDepartment, activeTab]);

  // Загрузка отчетов для всего отдела
  useEffect(() => {
    const loadAllReports = async () => {
      if (!selectedDepartment || activeTab !== "all") {
        return;
      }

      try {
        setIsLoadingReports(true);
        console.log("Начинаем загрузку всех отчетов отдела:", {
          group_id: selectedDepartment.value,
        });

        const response = await getHrInspectorReports({
          group_id: selectedDepartment.value,
          page_size: 1000,
        });

        console.log("Получены отчеты отдела:", response);

        // Сначала сортируем отчеты по дате
        const sortedReports = [...response.reports].sort(
          (a, b) => new Date(b.fact_date_start) - new Date(a.fact_date_start)
        );

        // Группировка отчетов по типам и периодам
        const groupedReports = {};
        let currentGroup = null;

        sortedReports.forEach((report) => {
          if (!report.fact_date_start || !report.portal_job_type_name) {
            console.warn(
              "Пропускаем отчет из-за отсутствия необходимых полей:",
              report
            );
            return;
          }

          const currentDate = new Date(report.fact_date_start);
          const employeeName = `${report.user}`.trim();

          // Если это первый отчет или новый тип отпуска или новый сотрудник, создаем новую группу
          if (
            !currentGroup ||
            currentGroup.type !== report.portal_job_type_name ||
            currentGroup.employeeName !== employeeName ||
            Math.abs(currentDate - new Date(currentGroup.endDate)) >
              24 * 60 * 60 * 1000
          ) {
            // Если была предыдущая группа, сохраняем ее
            if (currentGroup) {
              const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}_${currentGroup.employeeName}`;
              groupedReports[key] = currentGroup;
            }

            // Создаем новую группу
            currentGroup = {
              type: report.portal_job_type_name,
              startDate: report.fact_date_start,
              endDate: report.fact_date_start,
              employeeName: employeeName,
              reportIds: [report.id],
              leaveType: {
                label: report.portal_job_type_name,
              },
            };
          } else {
            // Продолжаем текущую группу
            currentGroup.endDate = report.fact_date_start;
            currentGroup.reportIds.push(report.id);
          }
        });

        // Не забываем добавить последнюю группу
        if (currentGroup) {
          const key = `${currentGroup.startDate}_${currentGroup.endDate}_${currentGroup.type}_${currentGroup.employeeName}`;
          groupedReports[key] = currentGroup;
        }

        console.log("Сгруппированные отчеты отдела:", groupedReports);
        setReports(Object.values(groupedReports));
      } catch (error) {
        console.error("Ошибка при загрузке отчетов отдела:", error);
        setError("Ошибка при загрузке отчетов");
      } finally {
        setIsLoadingReports(false);
      }
    };

    if (activeTab === "all") {
      loadAllReports();
    }
  }, [selectedDepartment, activeTab]);

  // История отпусков (только тип «Отпуск») по выбранному сотруднику
  useEffect(() => {
    const loadVacationHistory = async () => {
      if (activeTab !== "history" || !selectedEmployee) {
        setReports([]);
        return;
      }

      try {
        setIsLoadingReports(true);
        setError(null);

        const allReports = await fetchAllHrInspectorReportsForUser(
          selectedEmployee.value
        );
        setReports(groupVacationReportsIntoPeriods(allReports));
      } catch (loadError) {
        console.error("Ошибка при загрузке истории отпусков:", loadError);
        setReports([]);
        setError("Ошибка при загрузке истории отпусков");
      } finally {
        setIsLoadingReports(false);
      }
    };

    loadVacationHistory();
  }, [selectedEmployee, activeTab]);

  const handleDeleteReport = async (reportIds) => {
    setPendingDeleteReportIds(reportIds);
    setShowConfirmPopup(true);
  };

  const confirmDelete = async () => {
    try {
      setIsSubmitting(true);
      setShowConfirmPopup(false);

      // Последовательно удаляем все отчеты в периоде
      for (const reportId of pendingDeleteReportIds) {
        await deleteHrInspectorReport(reportId);
      }

      // Обновляем список отчетов после удаления
      setReports(
        reports.filter(
          (report) =>
            !report.reportIds.some((id) => pendingDeleteReportIds.includes(id))
        )
      );

      setError(null);
      setShowPopup(true);
      setTimeout(() => setShowPopup(false), 3000);
    } catch (error) {
      setError(`Ошибка при удалении отчета: ${error.message}`);
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsSubmitting(false);
      setPendingDeleteReportIds(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirmPopup(false);
    setPendingDeleteReportIds(null);
  };

  const openExportPopup = () => {
    setExportDates(getCurrentYearDateRange());
    setShowExportPopup(true);
  };

  const closeExportPopup = () => {
    if (isExportingSubstitutions) {
      return;
    }
    setShowExportPopup(false);
  };

  const handleExportDateChange = (date, type) => {
    setExportDates((prev) => {
      if (type === "start") {
        const endDate =
          prev.endDate && date && date > prev.endDate ? date : prev.endDate;
        return { startDate: date, endDate };
      }
      return { ...prev, endDate: date };
    });
  };

  const handleExportSubstitutions = async () => {
    if (
      isExportingSubstitutions ||
      !exportDates.startDate ||
      !exportDates.endDate
    ) {
      return;
    }

    setIsExportingSubstitutions(true);
    try {
      await exportSubstitutionsExcel({
        date_from: formatDateForApi(exportDates.startDate),
        date_to: formatDateForApi(exportDates.endDate),
      });
      setShowExportPopup(false);
    } catch (exportError) {
      console.error("Ошибка при выгрузке замещений:", exportError);
      setShowExportPopup(false);
      setError(exportError.message || "Ошибка при выгрузке замещений");
      setShowPopup(true);
      setTimeout(() => {
        setShowPopup(false);
        setError(null);
      }, 3000);
    } finally {
      setIsExportingSubstitutions(false);
    }
  };

  const renderSubstitutionsSidebar = () => {
    if (!isSubstitutionOperation(selectedLeaveType)) {
      return null;
    }

    return (
      <aside className="vacation-management__substitutions">
        <h3 className="vacation-management__substitutions-title">
          Замещения за последние {SUBSTITUTIONS_LOOKBACK_MONTHS} мес.
        </h3>
        {selectedEmployee && (
          <p className="vacation-management__substitutions-filter">
            По сотруднику: {selectedEmployee.label}
          </p>
        )}
        {isLoadingSubstitutions ? (
          <div className="loading">Загрузка замещений...</div>
        ) : substitutionsError ? (
          <div className="no-reports">{substitutionsError}</div>
        ) : substitutions.length > 0 ? (
          <ul className="substitutions-list">
            {substitutions.map((item) => (
              <li key={item.id} className="substitutions-list__item">
                <div className="substitutions-list__period">
                  {formatSubstitutionPeriod(item.start_date, item.end_date)}
                  {item.is_currently_active && (
                    <span className="substitutions-list__badge">Сейчас</span>
                  )}
                </div>
                <div className="substitutions-list__row">
                  <span className="substitutions-list__label">Отсутствует</span>
                  <span className="substitutions-list__value">
                    {formatSubstitutionUserName(item.absent_user)}
                  </span>
                </div>
                <div className="substitutions-list__row">
                  <span className="substitutions-list__label">Замещает</span>
                  <span className="substitutions-list__value">
                    {formatSubstitutionUserName(item.substitute_user)}
                  </span>
                </div>
                {item.reason && (
                  <div className="substitutions-list__reason">{item.reason}</div>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <div className="no-reports">
            {selectedEmployee
              ? "Нет замещений за последние 2 месяца для выбранного сотрудника"
              : "Нет замещений за последние 2 месяца"}
          </div>
        )}
      </aside>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "create":
        return (
          <div
            className={
              isSubstitutionOperation(selectedLeaveType)
                ? "vacation-management__create-layout"
                : ""
            }
          >
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Тип</label>
              <Select
                {...selectMenuProps}
                value={selectedLeaveType}
                onChange={handleLeaveTypeChange}
                options={leaveTypes}
                className="leave-type-select"
                classNamePrefix="leave-type-select"
                placeholder="Выберите тип"
                isLoading={isLoadingLeaveTypes}
                isDisabled={isLoadingLeaveTypes}
              />
            </div>

            {selectedLeaveType && (
              <>
                <div className="form-group">
                  <label>
                    {isSubstitutionOperation(selectedLeaveType)
                      ? "Отдел отсутствующего"
                      : "Отдел"}
                  </label>
                  <Select
                    {...selectMenuProps}
                    value={selectedDepartment}
                    onChange={(option) => {
                      setSelectedDepartment(option);
                    }}
                    options={departments}
                    className="department-select"
                    classNamePrefix="department-select"
                    placeholder={
                      isSubstitutionOperation(selectedLeaveType)
                        ? "Выберите отдел отсутствующего"
                        : "Выберите отдел"
                    }
                    isLoading={isLoadingDepartments}
                    isDisabled={isLoadingDepartments}
                  />
                </div>

                <div className="form-group">
                  <label>
                    {isSubstitutionOperation(selectedLeaveType)
                      ? "Отсутствующий сотрудник"
                      : "Сотрудник"}
                  </label>
                  <Select
                    {...selectMenuProps}
                    value={selectedEmployee}
                    onChange={(option) => {
                      setSelectedEmployee(option);
                      if (
                        option &&
                        selectedSubstituteEmployee?.value === option.value
                      ) {
                        setSelectedSubstituteEmployee(null);
                      }
                    }}
                    options={employees}
                    className="employee-select"
                    classNamePrefix="employee-select"
                    placeholder={
                      isSubstitutionOperation(selectedLeaveType)
                        ? "Выберите отсутствующего"
                        : "Выберите сотрудника"
                    }
                    isLoading={isLoadingEmployees}
                    isDisabled={isLoadingEmployees || !selectedDepartment}
                  />
                </div>

                {isSubstitutionOperation(selectedLeaveType) && (
                  <>
                    <div className="substitution-form__divider" />
                    <div className="form-group">
                      <label>Отдел замещающего</label>
                      <div className="form-hint">
                        Замещающий может быть из другого отдела
                      </div>
                      <Select
                        {...selectMenuProps}
                        value={selectedSubstituteDepartment}
                        onChange={setSelectedSubstituteDepartment}
                        options={departments}
                        className="department-select"
                        classNamePrefix="department-select"
                        placeholder="Выберите отдел замещающего"
                        isLoading={isLoadingDepartments}
                        isDisabled={isLoadingDepartments}
                      />
                    </div>

                    <div className="form-group">
                      <label>Замещающий сотрудник</label>
                      <Select
                        {...selectMenuProps}
                        value={selectedSubstituteEmployee}
                        onChange={setSelectedSubstituteEmployee}
                        options={substituteEmployees.filter(
                          (emp) => emp.value !== selectedEmployee?.value
                        )}
                        className="employee-select"
                        classNamePrefix="employee-select"
                        placeholder="Выберите замещающего"
                        isLoading={isLoadingSubstituteEmployees}
                        isDisabled={
                          isLoadingSubstituteEmployees ||
                          !selectedSubstituteDepartment ||
                          !selectedEmployee
                        }
                      />
                    </div>
                  </>
                )}

                {isCompensationOperation(selectedLeaveType) && (
                  <>
                    <div className="form-group">
                      <label>Месяц компенсации</label>
                      <Select
                        {...selectMenuProps}
                        value={compensationMonthOptions.find(
                          (m) => m.value === compensationMonth
                        )}
                        onChange={(option) =>
                          setCompensationMonth(option?.value ?? null)
                        }
                        options={compensationMonthOptions}
                        className="leave-type-select"
                        classNamePrefix="leave-type-select"
                        placeholder="Выберите месяц"
                        isSearchable={false}
                      />
                    </div>

                    <div className="form-group">
                      <label>Год</label>
                      <Select
                        {...selectMenuProps}
                        value={compensationYearOptions.find(
                          (y) => y.value === compensationYear
                        )}
                        onChange={(option) =>
                          setCompensationYear(option?.value ?? null)
                        }
                        options={compensationYearOptions}
                        className="leave-type-select"
                        classNamePrefix="leave-type-select"
                        placeholder="Выберите год"
                        isSearchable={false}
                      />
                    </div>

                    <div className="form-group">
                      <label>Тип дней</label>
                      <Select
                        {...selectMenuProps}
                        value={compensationDayBasisOptions.find(
                          (option) => option.value === compensationDayBasis
                        )}
                        onChange={(option) =>
                          setCompensationDayBasis(option?.value ?? null)
                        }
                        options={compensationDayBasisOptions}
                        className="leave-type-select"
                        classNamePrefix="leave-type-select"
                        placeholder="Выберите тип дней"
                        isSearchable={false}
                      />
                    </div>

                    <div className="form-group">
                      <label>Количество дней</label>
                      <input
                        type="number"
                        className="form-input"
                        min={1}
                        step={1}
                        value={compensationDays}
                        onChange={(e) => setCompensationDays(e.target.value)}
                        placeholder="Укажите количество дней"
                      />
                    </div>
                  </>
                )}

                {!isCompensationOperation(selectedLeaveType) && (
                <div className="form-group">
                  <label>Период</label>
                  {isTravelOperation(selectedLeaveType) && (
                    <div className="form-hint">
                      Для командировок записи создаются только на рабочие дни
                    </div>
                  )}
                  <div className="date-range-picker">
                    <DatePicker
                      selected={selectedDates.startDate}
                      onChange={(date) => handleDateChange(date, "start")}
                      selectsStart
                      startDate={selectedDates.startDate}
                      endDate={selectedDates.endDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Начало"
                      locale="ru"
                      calendarStartDay={1}
                      dayClassName={highlightDays}
                      filterDate={filterDate}
                      calendarClassName="custom-calendar"
                      renderDayContents={(day, date) => {
                        const dateString = date.toLocaleDateString("en-CA");
                        const dayData = calendarData[dateString];
                        return (
                          <div
                            className="day-content"
                            data-tooltip={dayData?.event_name || ""}
                          >
                            {day}
                          </div>
                        );
                      }}
                      className="date-picker"
                    />
                    <span className="date-range-separator">—</span>
                    <DatePicker
                      selected={selectedDates.endDate}
                      onChange={(date) => handleDateChange(date, "end")}
                      selectsEnd
                      startDate={selectedDates.startDate}
                      endDate={selectedDates.endDate}
                      minDate={selectedDates.startDate}
                      dateFormat="dd.MM.yyyy"
                      placeholderText="Конец"
                      locale="ru"
                      calendarStartDay={1}
                      dayClassName={highlightDays}
                      filterDate={filterDate}
                      calendarClassName="custom-calendar"
                      renderDayContents={(day, date) => {
                        const dateString = date.toLocaleDateString("en-CA");
                        const dayData = calendarData[dateString];
                        return (
                          <div
                            className="day-content"
                            data-tooltip={dayData?.event_name || ""}
                          >
                            {day}
                          </div>
                        );
                      }}
                      className="date-picker"
                    />
                  </div>
                </div>
                )}

                {isTravelOperation(selectedLeaveType) && (
                  <div className="form-group">
                    <label>Проект</label>
                    <Select
                      {...selectMenuProps}
                      value={selectedProject}
                      onChange={setSelectedProject}
                      options={projects}
                      className="project-select"
                      classNamePrefix="project-select"
                      placeholder="Выберите проект"
                      isLoading={isLoadingProjects}
                      isDisabled={isLoadingProjects || !selectedEmployee}
                    />
                  </div>
                )}

                {isSubstitutionOperation(selectedLeaveType) && (
                  <>
                    <div className="form-group">
                      <label>Причина</label>
                      <input
                        type="text"
                        className="form-input"
                        value={substitutionReason}
                        onChange={(e) => setSubstitutionReason(e.target.value)}
                        placeholder="Укажите причину замещения"
                      />
                    </div>

                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={substitutionDelegateApprovals}
                        onChange={(e) =>
                          setSubstitutionDelegateApprovals(e.target.checked)
                        }
                      />
                      Делегировать согласования
                    </label>

                    <label className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={substitutionDelegateRoles}
                        onChange={(e) =>
                          setSubstitutionDelegateRoles(e.target.checked)
                        }
                      />
                      Делегировать роли
                    </label>

                    <div className="form-group">
                      <label>Комментарий</label>
                      <textarea
                        className="form-textarea"
                        value={substitutionComment}
                        onChange={(e) => setSubstitutionComment(e.target.value)}
                        placeholder="Дополнительный комментарий (необязательно)"
                      />
                    </div>
                  </>
                )}
              </>
            )}

            <button
              className="submit-button"
              onClick={handleSubmit}
              disabled={
                !selectedLeaveType ||
                !selectedDepartment ||
                !selectedEmployee ||
                (isCompensationOperation(selectedLeaveType)
                  ? !compensationMonth ||
                    !compensationYear ||
                    !compensationDayBasis ||
                    !compensationDays ||
                    parseInt(compensationDays, 10) < 1
                  : !selectedDates.startDate || !selectedDates.endDate) ||
                (isTravelOperation(selectedLeaveType) && !selectedProject) ||
                (isSubstitutionOperation(selectedLeaveType) &&
                  (!selectedSubstituteDepartment ||
                    !selectedSubstituteEmployee ||
                    !substitutionReason.trim())) ||
                isSubmitting
              }
            >
              Сохранить
            </button>
          </div>
          {renderSubstitutionsSidebar()}
          </div>
        );
      case "edit":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Отдел</label>
              <Select
                {...selectMenuProps}
                value={selectedDepartment}
                onChange={(option) => {
                  setSelectedDepartment(option);
                }}
                options={departments}
                className="department-select"
                classNamePrefix="department-select"
                placeholder="Выберите отдел"
                isLoading={isLoadingDepartments}
                isDisabled={isLoadingDepartments}
              />
            </div>

            <div className="form-group">
              <label>Сотрудник</label>
              <Select
                {...selectMenuProps}
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                options={employees}
                className="employee-select"
                classNamePrefix="employee-select"
                placeholder="Выберите сотрудника"
                isLoading={isLoadingEmployees}
                isDisabled={isLoadingEmployees || !selectedDepartment}
              />
            </div>

            {selectedEmployee && selectedDepartment && (
              <div className="reports-list">
                {isLoadingReports ? (
                  <div className="loading">Загрузка отчетов...</div>
                ) : reports.length > 0 ? (
                  <div className="reports-table">
                    <div className="reports-table__header">
                      <div className="reports-table__cell">Период</div>
                      <div className="reports-table__cell">Тип</div>
                      <div className="reports-table__cell">Действия</div>
                    </div>
                    {reports.map((report) => (
                      <div
                        key={`${report.startDate}_${report.endDate}_${report.type}`}
                        className="reports-table__row"
                      >
                        <div className="reports-table__cell">
                          {new Date(report.endDate).toLocaleDateString("ru-RU")}
                          {report.startDate !== report.endDate && (
                            <>
                              {" — "}
                              {new Date(report.startDate).toLocaleDateString(
                                "ru-RU"
                              )}
                            </>
                          )}
                        </div>
                        <div className="reports-table__cell">
                          {report.leaveType?.label || "Неизвестный тип"}
                        </div>
                        <div className="reports-table__cell">
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteReport(report.reportIds)}
                            disabled={isSubmitting}
                          >
                            Удалить
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-reports">Нет отчетов для отображения</div>
                )}
              </div>
            )}
          </div>
        );
      case "all":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Отдел</label>
              <Select
                {...selectMenuProps}
                value={selectedDepartment}
                onChange={(option) => {
                  setSelectedDepartment(option);
                }}
                options={departments}
                className="department-select"
                classNamePrefix="department-select"
                placeholder="Выберите отдел"
                isLoading={isLoadingDepartments}
                isDisabled={isLoadingDepartments}
              />
            </div>

            {selectedDepartment && (
              <div className="reports-list">
                {isLoadingReports ? (
                  <div className="loading">Загрузка отчетов...</div>
                ) : reports.length > 0 ? (
                  <div className="reports-table">
                    <div className="reports-table__header">
                      <div className="reports-table__cell">Период</div>
                      <div className="reports-table__cell">Сотрудник</div>
                      <div className="reports-table__cell">Тип</div>
                    </div>
                    {reports.map((report) => (
                      <div
                        key={`${report.startDate}_${report.endDate}_${report.type}_${report.employeeName}`}
                        className="reports-table__row"
                      >
                        <div className="reports-table__cell">
                          {new Date(report.endDate).toLocaleDateString("ru-RU")}
                          {report.startDate !== report.endDate && (
                            <>
                              {" — "}
                              {new Date(report.startDate).toLocaleDateString(
                                "ru-RU"
                              )}
                            </>
                          )}
                        </div>
                        <div className="reports-table__cell">
                          {report.employeeName}
                        </div>
                        <div className="reports-table__cell">
                          {report.leaveType?.label || "Неизвестный тип"}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-reports">Нет отчетов для отображения</div>
                )}
              </div>
            )}
          </div>
        );
      case "history":
        return (
          <div className="vacation-management__form">
            <div className="form-group">
              <label>Сотрудник</label>
              <Select
                {...selectMenuProps}
                value={selectedEmployee}
                onChange={setSelectedEmployee}
                options={historyEmployees}
                className="employee-select"
                classNamePrefix="employee-select"
                placeholder={
                  isLoadingHistoryEmployees
                    ? "Загрузка сотрудников..."
                    : "Введите ФИО для поиска"
                }
                isLoading={isLoadingHistoryEmployees}
                isDisabled={isLoadingHistoryEmployees}
                isSearchable
                isClearable
                noOptionsMessage={({ inputValue }) =>
                  inputValue ? "Сотрудник не найден" : "Нет сотрудников"
                }
              />
            </div>

            {selectedEmployee && (
              <div className="reports-list">
                {isLoadingReports ? (
                  <div className="loading">Загрузка истории отпусков...</div>
                ) : error ? (
                  <div className="no-reports">{error}</div>
                ) : reports.length > 0 ? (
                  <div className="vacation-history">
                    <div className="reports-table reports-table--history">
                      <div className="reports-table__header">
                        <div className="reports-table__cell">Период</div>
                        <div className="reports-table__cell">Дней</div>
                      </div>
                      {groupVacationHistoryByYear(reports).map(
                        ({ year, periods, totalDays }) => (
                          <section
                            key={year}
                            className="vacation-history__year"
                          >
                            <div className="vacation-history__year-title">
                              {year}
                            </div>
                            {periods.map((period) => (
                              <div
                                key={`${year}_${period.startDate}_${period.endDate}`}
                                className="reports-table__row"
                              >
                                <div className="reports-table__cell">
                                  {formatVacationPeriod(period)}
                                </div>
                                <div className="reports-table__cell">
                                  {period.days}
                                </div>
                              </div>
                            ))}
                            <div className="reports-table__row reports-table__row--year-total">
                              <div className="reports-table__cell">
                                Итого за {year}
                              </div>
                              <div className="reports-table__cell">
                                {totalDays}
                              </div>
                            </div>
                          </section>
                        )
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="no-reports">
                    Нет записей об отпуске для этого сотрудника
                  </div>
                )}
              </div>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className={`vacation-management ${isSubmitting ? "submitting" : ""}`}>
      <h2 className="vacation-management__title">Кадровые операции</h2>

      <div className="sub-navigation">
        {subTabs.map((tab) => (
          <button
            key={tab.id}
            className={
              tab.id === activeTab
                ? "sub-navigation__button sub-navigation__button-active"
                : "sub-navigation__button"
            }
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
        <button
          type="button"
          className="sub-navigation__button vacation-management__export-substitutions"
          onClick={openExportPopup}
          disabled={isExportingSubstitutions}
        >
          ВЫГРУЗИТЬ ЗАМЕЩЕНИЯ
        </button>
      </div>

      {renderContent()}

      {showExportPopup && (
        <div className="popup-overlay">
          <div className="popup popup-confirm vacation-management__export-popup">
            <p className="popup-text">Период выгрузки замещений</p>
            <div className="date-range-picker vacation-management__export-dates">
              <DatePicker
                selected={exportDates.startDate}
                onChange={(date) => handleExportDateChange(date, "start")}
                selectsStart
                startDate={exportDates.startDate}
                endDate={exportDates.endDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Начало"
                locale="ru"
                calendarStartDay={1}
                calendarClassName="custom-calendar"
                className="date-picker"
                popperPlacement="bottom-start"
                disabled={isExportingSubstitutions}
              />
              <span className="date-range-separator">—</span>
              <DatePicker
                selected={exportDates.endDate}
                onChange={(date) => handleExportDateChange(date, "end")}
                selectsEnd
                startDate={exportDates.startDate}
                endDate={exportDates.endDate}
                minDate={exportDates.startDate}
                dateFormat="dd.MM.yyyy"
                placeholderText="Конец"
                locale="ru"
                calendarStartDay={1}
                calendarClassName="custom-calendar"
                className="date-picker"
                popperPlacement="bottom-start"
                disabled={isExportingSubstitutions}
              />
            </div>
            <div className="popup-buttons">
              <button
                type="button"
                className="popup-button popup-button-cancel"
                onClick={closeExportPopup}
                disabled={isExportingSubstitutions}
              >
                Отмена
              </button>
              <button
                type="button"
                className="popup-button popup-button-export"
                onClick={handleExportSubstitutions}
                disabled={
                  isExportingSubstitutions ||
                  !exportDates.startDate ||
                  !exportDates.endDate
                }
              >
                {isExportingSubstitutions ? "Выгрузка..." : "Выгрузить"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showConfirmPopup && (
        <div className="popup-overlay">
          <div className="popup popup-confirm">
            <p className="popup-text">
              Вы уверены, что хотите удалить этот период?
            </p>
            <div className="popup-buttons">
              <button
                className="popup-button popup-button-cancel"
                onClick={cancelDelete}
                disabled={isSubmitting}
              >
                Отмена
              </button>
              <button
                className="popup-button popup-button-confirm"
                onClick={confirmDelete}
                disabled={isSubmitting}
              >
                Удалить
              </button>
            </div>
          </div>
        </div>
      )}

      {showPopup && (
        <div className="popup-overlay">
          <div className={`popup ${error ? "popup-error" : ""}`}>
            <div
              className={`popup-icon ${error ? "popup-icon-error" : ""}`}
            ></div>
            <p className="popup-text">
              {error ||
                (activeTab === "create"
                  ? isSubstitutionOperation(selectedLeaveType)
                    ? "Замещение успешно создано"
                    : isCompensationOperation(selectedLeaveType)
                      ? "Компенсация успешно создана"
                      : "Отчет успешно создан"
                  : "Отчет успешно удален")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default VacationManagement;
